import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconLock,
  IconX,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type AlertVariant = "info" | "success" | "warning" | "error" | "readonly";

const VARIANT_COLOR: Record<AlertVariant, string> = {
  info: "info",
  success: "exito",
  warning: "advertencia",
  error: "peligro",
  readonly: "slate",
};

const VARIANT_ICON: Record<AlertVariant, typeof IconInfoCircle> = {
  info: IconInfoCircle,
  success: IconCircleCheck,
  warning: IconAlertTriangle,
  error: IconAlertCircle,
  readonly: IconLock,
};

export interface AlertProps {
  variant?: AlertVariant;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  onClose?: () => void;
  className?: string;
}

/**
 * `FeedbackAlert` · unifica los 41 usos de `<Alert>` con colores hoy
 * inconsistentes (hallazgo #3 de `INVENTARIO-UI.md`) en 5 variantes fijas.
 * Misma fórmula pastel que `Badge` (`light-dark()` + `color-mix()`), pero
 * con más padding e ícono+título+contenido en vez de un chip inline.
 */
export function Alert({ variant = "info", title, children, icon, onClose, className }: AlertProps) {
  const color = VARIANT_COLOR[variant];
  const Icon = VARIANT_ICON[variant];
  return (
    <div
      role="alert"
      className={cn("flex gap-3 rounded-md border p-3", className)}
      style={{
        backgroundColor: `light-dark(var(--color-${color}-50), color-mix(in srgb, var(--color-${color}-600) 20%, transparent))`,
        borderColor: `light-dark(var(--color-${color}-200), color-mix(in srgb, var(--color-${color}-400) 35%, transparent))`,
        color: `light-dark(var(--color-${color}-800), var(--color-${color}-200))`,
      }}
    >
      <span className="mt-0.5 shrink-0">{icon ?? <Icon size={18} />}</span>
      <div className="flex-1 text-sm">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "opacity-90")}>{children}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="shrink-0 self-start opacity-60 transition-opacity hover:opacity-100"
        >
          <IconX size={16} />
        </button>
      )}
    </div>
  );
}
